// src/helpers/posthogAnalytics.js

/**
 * PostHog Ecommerce Analytics Implementation
 * Follows PostHog's ecommerce events specification
 */

import { getPostHogClient } from "./posthogManager";
import { formatVariationToTitle } from "./formatters";
import { getVariantWeightPrice } from "./product";
import {
  POSTHOG_ANALYTICS_AFFILIATION,
  POSTHOG_ANALYTICS_BRAND,
  POSTHOG_ANALYTICS_CAMPAIGN_EMAIL_LANDING,
  POSTHOG_ANALYTICS_CART_VIEWED,
  POSTHOG_ANALYTICS_CHAT_OPENED,
  POSTHOG_ANALYTICS_CHECKOUT_STARTED,
  POSTHOG_ANALYTICS_CURRENCY,
  POSTHOG_ANALYTICS_ORDER_COMPLETED,
  POSTHOG_ANALYTICS_PAYMENT_STARTED,
  POSTHOG_ANALYTICS_PRODUCT_ADDED,
  POSTHOG_ANALYTICS_PRODUCT_CLICKED,
  POSTHOG_ANALYTICS_PRODUCT_REMOVED,
  POSTHOG_ANALYTICS_PRODUCT_VIEWED,
  POSTHOG_ANALYTICS_REPEAT_PURCHASE,
  POSTHOG_ANALYTICS_URL_BASE,
} from "../constants/AnalyticsConstants";

/**
 * Track when a product is viewed
 * @param {Object} product - Product data
 */
export const trackProductViewed = (product) => {
  const posthogClient = getPostHogClient();
  if (!posthogClient) return;

  posthogClient.capture(POSTHOG_ANALYTICS_PRODUCT_VIEWED, {
    product_id: product.id,
    sku: product.code,
    category: Array.isArray(product.categories)
      ? product.categories[0]
      : product.categories,
    name: product.name,
    brand: POSTHOG_ANALYTICS_BRAND,
    variant: product.variation
      ? formatVariationToTitle(product.variation)
      : null,
    price: product.price,
    quantity: 1,
    currency: POSTHOG_ANALYTICS_CURRENCY,
    url: `${POSTHOG_ANALYTICS_URL_BASE}/product/${product.url}`,
    image_url: product.image ? product.image[0] : null,
  });
};

/**
 * Track when a product is added to cart
 * @param {Object} product - Product data
 * @param {Number} quantity - Quantity added
 * @param {Object} context - Additional context data
 */
export const trackProductAdded = (product, quantity = 1, context = {}) => {
  const posthogClient = getPostHogClient();
  if (!posthogClient) return;

  posthogClient.capture(POSTHOG_ANALYTICS_PRODUCT_ADDED, {
    product_id: product.id,
    sku: product.code,
    category: Array.isArray(product.categories)
      ? product.categories[0]
      : product.categories,
    name: product.name,
    brand: POSTHOG_ANALYTICS_BRAND,
    variant: product.variation
      ? formatVariationToTitle(product.variation)
      : null,
    price: getVariantWeightPrice(product),
    quantity: quantity,
    url: `${POSTHOG_ANALYTICS_URL_BASE}/product/${product.url}`,
    image_url: product.image ? product.image[0] : null,
    ...context,
  });
};

/**
 * Track when a product is removed from cart
 * @param {Object} product - Product data
 * @param {Number} quantity - Quantity removed
 */
export const trackProductRemoved = (product, quantity = 1) => {
  const posthogClient = getPostHogClient();
  if (!posthogClient) return;

  posthogClient.capture(POSTHOG_ANALYTICS_PRODUCT_REMOVED, {
    product_id: product.id,
    sku: product.code,
    category: Array.isArray(product.categories)
      ? product.categories[0]
      : product.categories,
    name: product.name,
    brand: POSTHOG_ANALYTICS_BRAND,
    variant: product.variation
      ? formatVariationToTitle(product.variation)
      : null,
    price: getVariantWeightPrice(product),
    quantity: quantity,
    url: `${POSTHOG_ANALYTICS_URL_BASE}/product/${product.url}`,
    image_url: product.image ? product.image[0] : null,
  });
};

/**
 * Track when the cart is viewed
 * @param {Array} cartItems - Cart items from Redux
 */
export const trackCartViewed = (cartItems) => {
  const posthogClient = getPostHogClient();
  if (!posthogClient || !cartItems || cartItems.length === 0) return;

  const products = cartItems.map((item) => ({
    product_id: item.id,
    sku: item.code,
    category: Array.isArray(item.categories)
      ? item.categories[0]
      : item.categories,
    name: item.name,
    brand: POSTHOG_ANALYTICS_BRAND,
    variant: item.variation ? formatVariationToTitle(item.variation) : null,
    price: getVariantWeightPrice(item),
    quantity: item.quantity,
    url: `${POSTHOG_ANALYTICS_URL_BASE}/product/${item.url}`,
    image_url: item.image ? item.image[0] : null,
  }));

  posthogClient.capture(POSTHOG_ANALYTICS_CART_VIEWED, {
    products: products,
  });
};

/**
 * Track when checkout is started
 * @param {Array} cartItems - Cart items from Redux
 */
export const trackCheckoutStarted = (cartItems) => {
  const posthogClient = getPostHogClient();
  if (!posthogClient || !cartItems || cartItems.length === 0) return;

  const products = cartItems.map((item) => ({
    product_id: item.id,
    sku: item.code,
    category: Array.isArray(item.categories)
      ? item.categories[0]
      : item.categories,
    name: item.name,
    brand: POSTHOG_ANALYTICS_BRAND,
    variant: item.variation ? formatVariationToTitle(item.variation) : null,
    price: getVariantWeightPrice(item),
    quantity: item.quantity,
    url: `${POSTHOG_ANALYTICS_URL_BASE}/product/${item.url}`,
    image_url: item.image ? item.image[0] : null,
  }));

  posthogClient.capture(POSTHOG_ANALYTICS_CHECKOUT_STARTED, {
    affiliation: POSTHOG_ANALYTICS_AFFILIATION,
    products: products,
  });
};

/**
 * Track when payment is started
 * @param {Object} paymentData - Payment data including order details
 */
export const trackPaymentStarted = (paymentData) => {
  const posthogClient = getPostHogClient();
  if (!posthogClient || !paymentData) return;

  const products = paymentData.products.map((product) => ({
    product_id: product.productId,
    sku: product.code,
    category: Array.isArray(product.categories)
      ? product.categories[0]
      : product.categories,
    name: product.name,
    brand: POSTHOG_ANALYTICS_BRAND,
    variant: product.variant || null,
    price: product.originalPrice,
    quantity: product.quantity,
    url: `${POSTHOG_ANALYTICS_URL_BASE}/product/${
      product.url || product.productId
    }`,
    image_url: product.image ? product.image[0] : null,
  }));

  posthogClient.capture(POSTHOG_ANALYTICS_PAYMENT_STARTED, {
    checkout_id: paymentData.checkout_id || paymentData.orderId,
    order_id: paymentData.orderId,
    affiliation: POSTHOG_ANALYTICS_AFFILIATION,
    total: paymentData.totalPrice,
    subtotal: paymentData.totalPrice - (paymentData.delivery?.cost || 0),
    shipping: paymentData.delivery?.cost || 0,
    discount: paymentData.discount?.totalDiscount || 0,
    coupon: paymentData.discount?.code || null,
    currency: POSTHOG_ANALYTICS_CURRENCY,
    products: products,
  });
};

/**
 * Track when an order is completed
 * @param {Object} order - Order data
 */
export const trackOrderCompleted = (order) => {
  const posthogClient = getPostHogClient();
  if (!posthogClient || !order) return;

  const products = order.products.map((product) => ({
    product_id: product.productId,
    sku: product.code,
    category: Array.isArray(product.categories)
      ? product.categories[0]
      : product.categories,
    name: product.name,
    brand: POSTHOG_ANALYTICS_BRAND,
    variant: product.variant || null,
    price: product.originalPrice,
    quantity: product.quantity,
    url: `${POSTHOG_ANALYTICS_URL_BASE}/product/${
      product.url || product.productId
    }`,
    image_url: product.image ? product.image[0] : null,
  }));

  posthogClient.capture(POSTHOG_ANALYTICS_ORDER_COMPLETED, {
    checkout_id: order.checkout_id || order.orderId,
    order_id: order.orderId,
    affiliation: POSTHOG_ANALYTICS_AFFILIATION,
    total: order.totalPrice,
    subtotal: order.totalPrice - (order.delivery?.cost || 0),
    revenue: order.totalPrice,
    shipping: order.delivery?.cost || 0,
    discount: order.discount?.totalDiscount || 0,
    coupon: order.discount?.code || null,
    currency: POSTHOG_ANALYTICS_CURRENCY,
    products: products,
  });
};

/**
 * Triggers Product Clicked event in PostHog when clicking on a related product
 **/
export const trackRelatedProductClicked = (product, context = {}) => {
  const posthogClient = getPostHogClient();
  if (posthogClient) {
    posthogClient.capture(POSTHOG_ANALYTICS_PRODUCT_CLICKED, {
      product_id: product.id,
      sku: product.code,
      category: Array.isArray(product.categories)
        ? product.categories[0]
        : product.categories,
      name: product.name,
      brand: POSTHOG_ANALYTICS_BRAND,
      variant: product.variation
        ? formatVariationToTitle(product.variation)
        : null,
      price: product.price,
      quantity: 1,
      url: `${POSTHOG_ANALYTICS_URL_BASE}/product/${product.url}`,
      image_url: product.image ? product.image[0] : null,
      ...context,
    });
  }
};

/**
 * Track when an element (banner, section, etc.) becomes visible to the user
 * @param {String} elementId - Unique identifier for the element
 * @param {String} elementType - Type of element (e.g., 'banner', 'section', 'cta')
 * @param {Object} properties - Additional properties to track
 */
export const trackElementViewed = (elementId, elementType, properties = {}) => {
  const posthogClient = getPostHogClient();
  if (!posthogClient) return;

  posthogClient.capture('element_viewed', {
    element_id: elementId,
    element_type: elementType,
    page_url: window.location.pathname,
    timestamp: new Date().toISOString(),
    ...properties,
  });
};

/**
 * Track scroll depth on a page
 * @param {Number} scrollDepth - Percentage of page scrolled (0-100)
 * @param {Object} properties - Additional properties to track
 */
export const trackScrollDepth = (scrollDepth, properties = {}) => {
  const posthogClient = getPostHogClient();
  if (!posthogClient) return;

  posthogClient.capture('scroll_depth', {
    scroll_percentage: scrollDepth,
    page_url: window.location.pathname,
    timestamp: new Date().toISOString(),
    ...properties,
  });
};

/**
 * Track custom event with flexible properties
 * @param {String} eventName - Name of the event
 * @param {Object} properties - Event properties
 */
export const trackCustomEvent = (eventName, properties = {}) => {
  const posthogClient = getPostHogClient();
  if (!posthogClient) return;

  posthogClient.capture(eventName, {
    page_url: window.location.pathname,
    timestamp: new Date().toISOString(),
    ...properties,
  });
};

/**
 * Identify a user in PostHog with their unique ID and properties
 * This links all future events to this user
 * @param {String} userId - Unique user identifier (e.g., Firebase UID)
 * @param {Object} userProperties - User properties to store
 */
export const identifyUser = (userId, userProperties = {}) => {
  const posthogClient = getPostHogClient();
  if (!posthogClient || !userId) return;

  posthogClient.identify(userId, {
    ...userProperties,
    identified_at: new Date().toISOString(),
  });
};

/**
 * Reset user identification in PostHog (e.g., on logout)
 * Creates a new anonymous session
 */
export const resetUserIdentification = () => {
  const posthogClient = getPostHogClient();
  if (!posthogClient) return;

  posthogClient.reset();
};

/**
 * Update user properties without changing the user ID
 * @param {Object} properties - Properties to update
 */
export const updateUserProperties = (properties = {}) => {
  const posthogClient = getPostHogClient();
  if (!posthogClient) return;

  posthogClient.people.set(properties);
};

/**
 * Track when a product information tab is opened
 * @param {String} tabType - Type of tab (e.g., 'description', 'ingredients', 'custom', 'traceability')
 * @param {String} tabTitle - Title of the tab
 * @param {Object} product - Product data
 */
export const trackProductTabOpened = (tabType, tabTitle, product) => {
  const posthogClient = getPostHogClient();
  if (!posthogClient) return;

  posthogClient.capture('product_tab_opened', {
    tab_type: tabType,
    tab_title: tabTitle,
    product_id: product.id,
    product_name: product.name,
    product_category: Array.isArray(product.categories)
      ? product.categories[0]
      : product.categories,
    product_price: product.price,
    page_url: window.location.pathname,
    timestamp: new Date().toISOString(),
  });
};

/**
 * Track when discount code input is activated in cart
 * @param {Object} properties - Additional properties
 */
export const trackDiscountCodeActivatedInCart = (properties = {}) => {
  const posthogClient = getPostHogClient();
  if (!posthogClient) return;

  posthogClient.capture('discount_code_activated_in_cart', {
    page_url: window.location.pathname,
    timestamp: new Date().toISOString(),
    ...properties,
  });
};

/**
 * Track when discount code is applied in cart
 * @param {String} code - The discount code
 * @param {Boolean} valid - Whether the code was valid
 * @param {Object} properties - Additional properties
 */
export const trackDiscountCodeAppliedInCart = (code, valid, properties = {}) => {
  const posthogClient = getPostHogClient();
  if (!posthogClient) return;

  posthogClient.capture('discount_code_applied_in_cart', {
    code: code,
    valid: valid,
    page_url: window.location.pathname,
    timestamp: new Date().toISOString(),
    ...properties,
  });
};

/**
 * Track when user proceeds to checkout from cart
 * @param {Boolean} hasDiscountCode - Whether user has applied a discount code
 * @param {Object} properties - Additional properties
 */
export const trackCartProceedToCheckout = (hasDiscountCode = false, properties = {}) => {
  const posthogClient = getPostHogClient();
  if (!posthogClient) return;

  posthogClient.capture('cart_proceed_to_checkout', {
    has_discount_code: hasDiscountCode,
    page_url: window.location.pathname,
    timestamp: new Date().toISOString(),
    ...properties,
  });
};

/**
 * Track when discount code is removed from cart
 * @param {String} code - The discount code that was removed
 * @param {Object} properties - Additional properties
 */
export const trackDiscountCodeRemovedFromCart = (code, properties = {}) => {
  const posthogClient = getPostHogClient();
  if (!posthogClient) return;

  posthogClient.capture('discount_code_removed_from_cart', {
    code: code,
    page_url: window.location.pathname,
    timestamp: new Date().toISOString(),
    ...properties,
  });
};

/**
 * Track when a visitor lands on the webshop from a campaign email.
 * Fires when UTM parameters are present and the medium is "email".
 * @param {Object} utmParams - UTM parameters captured from the URL
 * @param {Object} properties - Additional properties (e.g. segment, mailType)
 */
export const trackCampaignEmailLanding = (utmParams, properties = {}) => {
  const posthogClient = getPostHogClient();
  if (!posthogClient || !utmParams) return;

  posthogClient.capture(POSTHOG_ANALYTICS_CAMPAIGN_EMAIL_LANDING, {
    utm_source: utmParams.source || null,
    utm_medium: utmParams.medium || null,
    utm_campaign: utmParams.campaign || null,
    utm_term: utmParams.term || null,
    utm_content: utmParams.content || null,
    landing_url: window.location.pathname,
    timestamp: new Date().toISOString(),
    ...properties,
  });
};

/**
 * Track a repeat purchase attributed to a campaign email.
 * @param {Object} order - Completed order data
 * @param {Object} properties - Additional properties (e.g. UTM params, segment, mailType)
 */
export const trackRepeatPurchase = (order, properties = {}) => {
  const posthogClient = getPostHogClient();
  if (!posthogClient || !order) return;

  posthogClient.capture(POSTHOG_ANALYTICS_REPEAT_PURCHASE, {
    order_id: order.orderId,
    total: order.totalPrice,
    discount: order.discount?.totalDiscount || 0,
    coupon: order.discount?.code || null,
    currency: POSTHOG_ANALYTICS_CURRENCY,
    ...properties,
  });
};

/**
 * Track when the visitor opens the Digital Chat widget.
 * Session replays with the chat open can be filtered on this event.
 */
export const trackChatOpened = () => {
  const posthogClient = getPostHogClient();
  if (!posthogClient) return;

  posthogClient.capture(POSTHOG_ANALYTICS_CHAT_OPENED, {
    page_url: window.location.pathname,
  });
};
